import {
  Field,
  Form,
  Formik,
  FormikConfig,
  FormikProps,
  FormikValues,
} from 'formik';
import * as React from 'react';

import clsx from 'clsx';

export type DynamicField<V = any> = {
  name: string;
  label?: string;
  component: React.ComponentType<V>;
};

type Props<Values extends FormikValues = FormikValues> = {
  initialValues: Values;
  fields: DynamicField[];
  fieldRender?: (
    form: FormikProps<Values>,
    field: any,
    index: number,
  ) => JSX.Element | null;
  submit?: React.ReactElement<any, any>;
  bottomFields?: DynamicField[];
} & FormikConfig<Values>;

function DynamicForm<Values extends FormikValues>(
  {
    initialValues,
    fields,
    onSubmit,
    fieldRender,
    submit,
    bottomFields,
    ...props
  }: Props<Values>,
  ref: React.ForwardedRef<FormikProps<Values>>,
) {
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      innerRef={ref}
      {...props}
    >
      {form => {
        const submitButton =
          submit &&
          React.cloneElement(submit, {
            onClick: () => form.submitForm(),
            disabled:
              submit?.props?.disabled || !form.isValid || form.isSubmitting,
            className: clsx(submit.props.className, {
              disabled: !form.isValid || form.isSubmitting,
            }),
          });

        return (
          <Form>
            {fields.map((field, index) => {
              if (typeof fieldRender === 'function') {
                return fieldRender(form, field, index);
              }
              return <Field key={field.name} {...field} />;
            })}
            {submitButton}
            {bottomFields?.map((field, index) =>
              typeof fieldRender === 'function'
                ? fieldRender(form, field, index)
                : null,
            )}
          </Form>
        );
      }}
    </Formik>
  );
}

export default React.forwardRef(DynamicForm) as <Values extends FormikValues>(
  props: Props<Values> & { ref?: React.ForwardedRef<FormikProps<Values>> },
) => ReturnType<typeof DynamicForm>;
